import { formatDate } from '@app/functions/date-format';
import { Candidate } from '@app/models/offer-letters/candidate.model';
import { OfferLetter } from '@app/models/offer-letters/offer-letter.model';
import { Column } from '@app/modules/table/classes/column.class';
import { CellDisplay } from '@app/modules/table/enums/cell-display.enum';
import { Translatable } from '@app/types/translatable.type';

export class OfferStatusColumn extends Column {
    title = 'table.offer-letter.offerStatus';
    type: CellDisplay = CellDisplay.string;

    candidate: Candidate;

    constructor(
        candidate: Candidate,
        private currentLang: string
    ) {
        super();

        this.candidate = candidate;
    }

    /**
     * The order of steps matter here since:
     * - an offer letter cannot be accepted if its withdrawn or declined.
     * - an offer letter cannot be expired if its withdrawn or declined or accepted.
     */
    getDisplayPropertyValue(offerLetter: OfferLetter): Translatable {
        if (offerLetter.isWithdrawn()) {
            return offerLetter.withdrawnAt
                ? {
                      key: 'table.offer-status.withdrawnOn',
                      params: { withdrawnOnDate: formatDate(offerLetter.withdrawnAt, 'short', this.currentLang) },
                  }
                : '';
        }

        if (offerLetter.isDeclined()) {
            return offerLetter.declinedAt
                ? {
                      key: 'table.offer-status.declinedOn',
                      params: { declinedOnDate: formatDate(offerLetter.declinedAt, 'short', this.currentLang) },
                  }
                : '';
        }

        if (offerLetter.isAccepted()) {
            return offerLetter.signedAt
                ? {
                      key: 'table.offer-status.acceptedOn',
                      params: { signedAtDate: formatDate(offerLetter.signedAt, 'short', this.currentLang) },
                  }
                : '';
        }

        if (offerLetter.isExpired() && !this.candidate.isHired()) {
            return offerLetter.expiresOn
                ? {
                      key: 'table.offer-status.expiredOn',
                      params: { expiredDate: formatDate(offerLetter.expiresOn, 'short', this.currentLang) },
                  }
                : '';
        }

        if (this.candidate.hasSeen()) {
            return this.candidate.firstSeenAt
                ? {
                      key: 'table.offer-status.seenOn',
                      params: { seenDate: formatDate(this.candidate.firstSeenAt, 'short', this.currentLang) },
                  }
                : '';
        }

        // Candidate could be hired directly even tho offer letter could still be pending.
        if (offerLetter.isPending() && !this.candidate.isHired()) {
            return 'table.offer-status.pending';
        }

        return '-';
    }
}
